import {
  addMonths,
  differenceInMonths,
  eachDayOfInterval,
  format,
  isWithinInterval,
  lastDayOfMonth,
  parse,
  startOfMonth,
} from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { DateRange, YearGroup } from "../types";
import {
  EnumEventType,
  EventLookaheadMonthsQuery,
  EventManyQuery,
  EventStatus,
  useEventLookaheadMonthsQuery,
  useEventManyLazyQuery,
  useWidgetByIdLazyQuery,
} from "gql/generated";
import { EVENT_DATE_FORMAT, getMultilingualString } from "shared/utils";
import { MultilingualLangKey } from "shared/types";
import _ from "lodash";
import { EventsWidgetListDisplayMode } from "../EventsWidget";

type UseEventsWidgetProps = {
  hostElement?: HTMLElement & {
    carouselEventsLoaded?: (e: CustomEvent) => void;
  };
  organizationId: string;
  widgetId?: string;
  lang?: MultilingualLangKey;
  widgetDisplayMode?: EventsWidgetListDisplayMode;
  applyFilters?: string[];
  nextNMonths?: number;
};

export const useEventsWidget = ({
  hostElement,
  organizationId,
  widgetId,
  lang,
  widgetDisplayMode = "grid",
  applyFilters = [],
  nextNMonths = 0,
}: UseEventsWidgetProps) => {
  /**
   * These values are used to store the widget details
   * - displayMode is the display mode for the widget
   * - eventTags are the tags for the widget
   * - lookAheadMonths is the number of months to look ahead for the widget
   */
  const [displayMode, setDisplayMode] =
    useState<EventsWidgetListDisplayMode>(widgetDisplayMode);
  const [eventTags, setEventTags] = useState<string[]>(applyFilters);
  const [lookAheadMonths, setLookAheadMonths] = useState<number>(nextNMonths);
  // If the widget is invalid (e.g. the owner organizationId does not match the organizationId) OR an error occurs,
  // we need to set the isInvalidWidget state to true
  const [isInvalidWidget, setIsInvalidWidget] = useState(false);

  // If we have a widgetId, we need to fetch the widget details because they will override the values of
  // applyFilters, widgetDisplayMode, and nextNMonths
  const [loadWidgetDetails, { loading: loadingWidgetDetails }] =
    useWidgetByIdLazyQuery();
  useEffect(() => {
    if (widgetId) {
      loadWidgetDetails({
        variables: { id: widgetId },
        onCompleted: data => {
          if (data.widgetById) {
            if (data.widgetById.owner.entityId !== organizationId) {
              setIsInvalidWidget(true);
            }
            setEventTags(data.widgetById.eventSettings?.eventTags ?? []);
            setDisplayMode(
              (data.widgetById.eventSettings?.displayType.toLowerCase() as EventsWidgetListDisplayMode) ??
                "grid",
            );
            setLookAheadMonths(
              data.widgetById.eventSettings?.lookAheadMonths ?? 0,
            );
          } else {
            setIsInvalidWidget(true);
          }
        },
        onError: () => {
          setIsInvalidWidget(true);
        },
      });
    }
  }, [organizationId, widgetId, loadWidgetDetails]);

  /**
   * Store the date range for the events that will be displayed
   * - isFullMonth is true ONLY when the range is from the 1st of the month
   *   to the last day of the month (in the same month)
   * - When isFullMonth is true, the recurring events section will be displayed
   * - When isFullMonth is false, any recurring events in the date range are shown
   *   as cards and the featured events sections is renamed to "Events"
   */
  const [dateRange, setDateRange] = useState<DateRange>(
    getDefaultDateRange(
      format(startOfMonth(new Date()), EVENT_DATE_FORMAT),
      lookAheadMonths,
    ),
  );
  useEffect(() => {
    setDateRange(
      getDefaultDateRange(
        format(startOfMonth(new Date()), EVENT_DATE_FORMAT),
        lookAheadMonths,
      ),
    );
  }, [lookAheadMonths]);

  /**
   * The backend returns a JSON object with the months and event counts { "2024-01": 1, "2024-02": 2, ... }
   * We need to convert this into a yearGroup array so we can easily display the months in the MonthYearSelector
   * [ { year: "2024", monthsAndEventCounts: [{ month: "2024-01", eventCount: 1 }, { month: "2024-02", eventCount: 2 }, ... ] }, ... ]
   */
  const [yearGroups, setYearGroups] = useState<YearGroup[]>([]);

  /**
   * We'll also store the following values to easily retrieve them instead of looping through the yearGroups
   */
  const [today] = useState<string>(format(new Date(), EVENT_DATE_FORMAT));
  const [firstMonthOfYearWithEvents, setFirstMonthOfYearWithEvents] = useState<{
    [key: string]: string;
  }>({});
  const [calendarMinDate, setCalendarMinDate] = useState<string>(today);
  const [calendarMaxDate, setCalendarMaxDate] = useState<string>(today);
  const [calendarSkippedDates, setCalendarSkippedDates] = useState<string[]>(
    [],
  );

  /**
   * Get the months and event counts for the lookahead months
   * - When the data loads, it is parsed to get the min/max dates for the calendar,
   *   the skipped dates, yearGroups, firstMonthOfYearWithEvents, and dateRange
   * - If the 1st month has NO events, we need to skip ahead to the next month
   *   with events and in that month, the first card needs to show a message
   *   that there are no events for the month that was skipped
   * - These values are all passed into the EventsContextProvider
   */
  const {
    data: monthsAndEventCounts,
    loading: loadingMonthsAndEventCounts,
    error: errorMonthsAndEventCounts,
  } = useEventLookaheadMonthsQuery({
    variables: { organizationId },
    onCompleted: data => {
      if (data.eventLookaheadMonths) {
        const {
          calendarMinDate,
          calendarMaxDate,
          calendarSkippedDates,
          yearGroups,
          firstMonthOfYearWithEvents,
          dateRange,
        } = getImportantDateInformation(
          data.eventLookaheadMonths,
          lookAheadMonths,
        );

        // Now convert these months into the min/max and skipped calendar dates
        setCalendarMinDate(calendarMinDate);
        setCalendarMaxDate(calendarMaxDate);
        setCalendarSkippedDates(calendarSkippedDates);
        setYearGroups(yearGroups);
        setFirstMonthOfYearWithEvents(firstMonthOfYearWithEvents);
        setDateRange(dateRange);
      }
    },
  });

  /**
   * Store the events that are within the date range
   * - This array is split into featuredEvents and recurringEvents OR
   *   when isFullMonth is false, all events go into the featuredEvents array
   * - This is also used to determine the tags for the filter carousel
   */
  const [allCurrentEvents, setAllCurrentEvents] = useState<
    EventManyQuery["eventMany"]
  >([]);

  /**
   * When the month changes, we need to update the dateRange
   * @param dir
   */
  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
    queryNewEventsByDateRange(newDateRange);
  };

  /**
   * Handle changes to the date range (from the Select dates... button)
   * - If the yyyy-MM is the same as current, we can just filter the data
   * - If the yyyy-MM is different, we need to query the DB
   * @param newDateRange
   */
  const handleSelectedDatesChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
    /** Only run a query if the date range has changed */
    if (
      newDateRange.from !== dateRange.from ||
      newDateRange.to !== dateRange.to
    ) {
      const newFromMonth = newDateRange.from.substring(5, 7);
      const newToMonth = newDateRange.to.substring(5, 7);
      const currentFromMonth = dateRange.from.substring(5, 7);
      const currentToMonth = dateRange.to.substring(5, 7);

      // Only run a query if the month of the newDateRange has changed
      // Otherwise use the current events & further filter them based on the
      // newDateRange
      if (
        newFromMonth !== currentFromMonth ||
        newToMonth !== currentToMonth ||
        (newDateRange.isFullMonth && !dateRange.isFullMonth)
      ) {
        queryNewEventsByDateRange(newDateRange);
      } else {
        const filteredEvents = filterEventsByDateRange(
          allCurrentEvents,
          newDateRange,
        );
        expandEvents(filteredEvents, newDateRange);
        determineTags(filteredEvents);
        filterEventsByTags(newDateRange);
      }
    }
  };

  /**
   * Determine if the date range is 10 months in the future
   * @param newRange
   * @returns
   */
  const getIs10MonthsInFuture = useCallback(
    (newRange: DateRange) =>
      Math.abs(
        differenceInMonths(
          parse(newRange.from, EVENT_DATE_FORMAT, new Date()),
          startOfMonth(new Date()),
        ),
      ) >= 10,
    [],
  );

  /**
   * Determine the view mode for the date range
   * - If there are NO featured events AND there ARE recurring events AND we're in month view,
   *   move the recurring events into the featured events array AND set the date range to show
   *   the grid view
   * - If the widgetDisplayMode is carousel, set the date range to show the grid view
   * - If the date range is in the future, set the date range to show the future event notice
   */
  const determineDateRangeViewMode = useCallback(
    (
      newRange: DateRange,
      is10MonthsInFuture: boolean,
      forceGridView?: boolean,
    ) => {
      const gridView =
        // Use the value passed into the function if it exists,
        // otherwise calculate it
        forceGridView ??
        ((allFeaturedEvents.current.length === 0 &&
          allRecurringEvents.current.length > 0 &&
          newRange.isFullMonth) ||
          (allFeaturedEvents.current.length > 0 &&
            allRecurringEvents.current.length === 0 &&
            newRange.isFullMonth) ||
          displayMode === "carousel");

      // Update the view
      setDateRange(prev => ({
        ...prev,
        forceGridView: gridView,
        showFutureEventNotice: is10MonthsInFuture,
      }));

      // If there are NO featured events AND there ARE recurring events AND we're in month view,
      // move the recurring events into the featured events array AND set the date range to show
      // the grid view
      if (
        allFeaturedEvents.current.length === 0 &&
        allRecurringEvents.current.length > 0 &&
        newRange.isFullMonth
      ) {
        allFeaturedEvents.current = [...allRecurringEvents.current];
        allRecurringEvents.current = [];
      }

      // If the display mode is carousel, we need to set the date range to show the grid view
      if (displayMode === "carousel") {
        setDateRange(prev => ({
          ...prev,
          forceGridView: true,
        }));
      }
    },
    [displayMode],
  );

  /**
   * When the new events are loaded, we need to split them into
   * featuredEvents and recurringEvents arrays.
   * The items in featuredEvents are type SINGLE or MULTI
   * OR recurring where the start yyyy-MM is the same as the date range.
   * The items in the recurringEvents are type RECURRING
   * where the start yyyy-MM is NOT the same as the date range.
   * This allows a recurring event to be featured in its first month
   * and then be "relegated" to the recurring events section after.
   */
  const allFeaturedEvents = useRef<EventManyQuery["eventMany"]>([]);
  const allRecurringEvents = useRef<EventManyQuery["eventMany"]>([]);
  const [featuredEvents, setFeaturedEvents] = useState<
    EventManyQuery["eventMany"]
  >([]);
  const [recurringEvents, setRecurringEvents] = useState<
    EventManyQuery["eventMany"]
  >([]);
  const expandEvents = useCallback(
    (events: EventManyQuery["eventMany"], newRange: DateRange) => {
      // Get the number of months in the future
      const is10MonthsInFuture = getIs10MonthsInFuture(newRange);

      // Filter to the featured events and sort by startDate
      allFeaturedEvents.current = _.sortBy(
        events.filter(
          event =>
            event.type === EnumEventType.Single ||
            event.type === EnumEventType.Multi ||
            !newRange.isFullMonth ||
            displayMode === "carousel" ||
            is10MonthsInFuture,
        ),
        "occurrences[0]",
      );

      // Get all of the recurring events
      allRecurringEvents.current = _.sortBy(
        events.filter(
          event =>
            newRange.isFullMonth &&
            !is10MonthsInFuture &&
            displayMode === "grid" &&
            event.type === EnumEventType.Recurring,
        ),
        "occurrences[0]",
      );
      determineDateRangeViewMode(newRange, is10MonthsInFuture);
    },
    [determineDateRangeViewMode, getIs10MonthsInFuture, displayMode],
  );

  /**
   * Filter the allFeaturedEvents and allRecurringEvents by the selected tag
   * There is a Ref so it can be used in other methods without needing to be
   * a dependency, and a State so it can be passed into components
   */
  const selectedFilterTagRef = useRef("");
  const [selectedFilterTag, setSelectedFilterTag] = useState("");
  const filterEventsByTags = useCallback(
    (newDateRange?: DateRange) => {
      newDateRange = newDateRange ?? dateRange;
      if (!selectedFilterTagRef.current) {
        setFeaturedEvents([...allFeaturedEvents.current]);
        setRecurringEvents([...allRecurringEvents.current]);
        determineDateRangeViewMode(
          newDateRange,
          getIs10MonthsInFuture(newDateRange),
        );
      } else {
        // Put all of the events into the same array and force the single view
        const filteredEvents = allCurrentEvents.filter(
          event =>
            event.tags?.global.some(
              tag => tag._id === selectedFilterTagRef.current,
            ) ||
            event.tags?.organizationSpecific.some(
              tag => tag._id === selectedFilterTagRef.current,
            ),
        );
        setFeaturedEvents(_.sortBy(filteredEvents, "occurrences[0]"));
        determineDateRangeViewMode(
          newDateRange,
          getIs10MonthsInFuture(newDateRange),
          true,
        );
      }
    },
    [
      allCurrentEvents,
      dateRange,
      determineDateRangeViewMode,
      getIs10MonthsInFuture,
    ],
  );

  /**
   * When the new events are loaded, we need to re-create the available
   * filters. If there is a selectedFilter, check to make sure it exists
   * in the new filterTags list.
   * If not, reset the selectedFilterTagRef
   * If so, filter the event results
   */
  const [filterTags, setFilterTags] = useState<
    { name: string; _id: string; count: number }[]
  >([]);
  const determineTags = useCallback(
    (events: EventManyQuery["eventMany"]) => {
      const allTags = Array.from(
        _.uniqBy(
          events.flatMap(event => [
            ...(event.tags?.global
              .map(tag => ({
                name: getMultilingualString(tag.name, lang),
                _id: tag._id,
                count: 0,
              }))
              .filter(tag => !!tag?._id) ?? []),
            ...(event.tags?.organizationSpecific
              ?.map(tag => ({
                name: getMultilingualString(tag.name, lang),
                _id: tag._id,
                count: 0,
              }))
              .filter(tag => !!tag?._id) ?? []),
          ]),
          "_id",
        ),
      ) as { name: string; _id: string; count: number }[];

      setFilterTags(
        _.sortBy(
          allTags.map(tag => ({
            name: tag.name,
            _id: tag._id,
            count: events.filter(
              event =>
                event.tags?.global.some(
                  t => getMultilingualString(t.name, lang) === tag.name,
                ) ||
                event.tags?.organizationSpecific.some(
                  t => getMultilingualString(t.name, lang) === tag.name,
                ),
            ).length,
          })),
          "name",
        ),
      );
      if (!allTags.some(tag => tag._id === selectedFilterTagRef.current)) {
        selectedFilterTagRef.current = "";
      }
    },
    [lang],
  );

  /**
   * When the filter tag is changed, we need to re-filter the events
   * - Set the Ref so the methods that use it will have the most recent value
   * - Set the State so the components that use it will refresh
   * @param id
   */
  const handleFilterChange = (id: string) => {
    selectedFilterTagRef.current =
      selectedFilterTagRef.current === id ? "" : id;
    setSelectedFilterTag(selectedFilterTagRef.current);
    filterEventsByTags();
  };

  /**
   * When in carousel mode, we'll dispatch an event when the data is loaded
   * - This is so the parent application can update the UI based on having data/no data
   */
  const sendCarouselEventsLoadedEvent = useCallback(
    (events: EventManyQuery["eventMany"]) => {
      if (!hostElement || widgetDisplayMode !== "carousel") return;

      const customEvent = new CustomEvent("carouselEventsLoaded", {
        detail: {
          hasEvents: events.length > 0,
          eventCount: events.length,
        },
        bubbles: true,
        composed: true,
      });

      const hasJsMethodAttached =
        typeof hostElement.carouselEventsLoaded === "function";
      const jsCallbackName = hostElement.getAttribute("carouselEventsLoaded");

      if (hasJsMethodAttached) {
        // The elemnt has a JS method attached to it like:
        // hostElement.carouselEventsLoaded = e => {}
        hostElement.carouselEventsLoaded?.(customEvent);
      } else if (
        jsCallbackName &&
        typeof window[jsCallbackName as keyof Window] === "function"
      ) {
        // The element has an attribute like:
        // <whereabouts-events-widget carouselEventsLoaded="handleCarouselEventsLoaded">
        // AND handleCarouselEventsLoaded is a global function
        window[jsCallbackName as keyof Window](customEvent);
      } else {
        // The element has an event listener like:
        // hostElement.addEventListener("carouselEventsLoaded", e => {})
        hostElement.dispatchEvent(customEvent);
      }
    },
    [hostElement, widgetDisplayMode],
  );

  const [getEvents, { error, loading, called }] = useEventManyLazyQuery();

  const queryNewEventsByDateRange = useCallback(
    (newDateRange: DateRange) => {
      getEvents({
        variables: {
          startDate: newDateRange.from,
          endDate: newDateRange.to,
          organizationId,
          status: EventStatus.Approved,
          ...(eventTags.length > 0 && { tags: eventTags }),
        },
      }).then(response => {
        if (response.data?.eventMany) {
          sendCarouselEventsLoadedEvent(response.data?.eventMany);
          setAllCurrentEvents(response.data.eventMany);
          expandEvents(response.data.eventMany, newDateRange);
          determineTags(response.data.eventMany);
          filterEventsByTags(newDateRange);
        } else {
          sendCarouselEventsLoadedEvent([]);
        }
      });
    },
    [
      sendCarouselEventsLoadedEvent,
      determineTags,
      expandEvents,
      filterEventsByTags,
      getEvents,
      organizationId,
      eventTags,
    ],
  );

  /**
   * Get all the events based on the initial dateRange on first load
   * - We delay the loading until the monthsAndEventCounts have been loaded
   */
  useEffect(() => {
    if (
      !called &&
      !loadingMonthsAndEventCounts &&
      !errorMonthsAndEventCounts &&
      yearGroups.length > 0
    ) {
      queryNewEventsByDateRange(dateRange);
    }
  }, [
    called,
    dateRange,
    loadingMonthsAndEventCounts,
    errorMonthsAndEventCounts,
    yearGroups,
    queryNewEventsByDateRange,
  ]);

  return {
    isInvalidWidget,
    displayMode,
    eventTags,
    lookAheadMonths,
    loadingWidgetDetails,
    monthsAndEventCounts: monthsAndEventCounts?.eventLookaheadMonths,
    loadingMonthsAndEventCounts,
    errorMonthsAndEventCounts,
    dateRange,
    featuredEvents,
    recurringEvents,
    error,
    loading,
    filterTags,
    selectedFilterTag,
    yearGroups,
    firstMonthOfYearWithEvents,
    calendarMaxDate,
    calendarMinDate,
    calendarSkippedDates,
    handleFilterChange,
    handleDateRangeChange,
    handleSelectedDatesChange,
  };
};

/**
 * Convert the monthsAndEventCounts object into a yearGroup array
 * @param monthsAndEventCounts
 * @returns
 */
const convertMonthsAndEventCountsToYearGroups = (
  monthsAndEventCounts: EventLookaheadMonthsQuery["eventLookaheadMonths"],
) => {
  let year = "";
  const newYearGroups: YearGroup[] = [];
  let currentYearGroup: YearGroup | null = null;
  Object.keys(monthsAndEventCounts)
    .sort()
    .forEach(key => {
      if (key.substring(0, 4) !== year) {
        year = key.substring(0, 4);
        currentYearGroup = {
          year,
          monthsAndEventCounts: [],
        };
        newYearGroups.push(currentYearGroup);
      }
      currentYearGroup?.monthsAndEventCounts.push({
        month: key,
        eventCount: monthsAndEventCounts[key],
      });
    });
  return newYearGroups;
};

/**
 * Get the important date information from the monthsAndEventCounts object
 * @param monthsAndEventCounts
 * @returns
 */
const getImportantDateInformation = (
  monthsAndEventCounts: EventLookaheadMonthsQuery["eventLookaheadMonths"],
  nextNMonths: number,
) => {
  // Ensure the keys in the object are sorted
  const sortedKeys = Object.keys(monthsAndEventCounts).sort();

  // Find the first month with events (Default to the first month if no events in any month)
  const firstMonthWithEvents =
    sortedKeys.find(key => monthsAndEventCounts[key] > 0) ?? sortedKeys[0];

  // Find the last month with events (Default to the last month if no events in any month)
  const lastMonthWithEvents =
    sortedKeys.reverse().find(key => monthsAndEventCounts[key] > 0) ??
    sortedKeys[sortedKeys.length - 1];

  // Find the months with no events
  const monthsWithNoEvents = sortedKeys.filter(
    key => monthsAndEventCounts[key] === 0,
  );

  // Determine the min and max dates for the calendar
  const today = format(new Date(), EVENT_DATE_FORMAT);
  const calendarMinDate =
    today > firstMonthWithEvents ? today : firstMonthWithEvents;
  const calendarMaxDate = format(
    lastDayOfMonth(parse(lastMonthWithEvents, EVENT_DATE_FORMAT, new Date())),
    EVENT_DATE_FORMAT,
  );

  // Determine the dates that are skipped for the calendar
  const calendarSkippedDates = monthsWithNoEvents.flatMap(month =>
    eachDayOfInterval({
      start: parse(month, EVENT_DATE_FORMAT, new Date()),
      end: lastDayOfMonth(parse(month, EVENT_DATE_FORMAT, new Date())),
    }).map(date => format(date, EVENT_DATE_FORMAT)),
  );

  // Convert the monthsAndEventCounts object into a yearGroup array
  const yearGroups =
    convertMonthsAndEventCountsToYearGroups(monthsAndEventCounts);

  // Determine the first month of each year with events
  const firstMonthOfYearWithEvents: { [key: string]: string } = {};
  yearGroups.forEach(yearGroup => {
    const firstMonthWithEvents =
      yearGroup.monthsAndEventCounts.find(month => month.eventCount > 0)
        ?.month ?? "";
    firstMonthOfYearWithEvents[yearGroup.year] = firstMonthWithEvents;
  });

  // Determine the date range to display (based on the first month with events)
  const dateRange = getDefaultDateRange(firstMonthWithEvents, nextNMonths);

  return {
    lastMonthWithEvents,
    monthsWithNoEvents,
    calendarMinDate,
    calendarMaxDate,
    calendarSkippedDates,
    yearGroups,
    firstMonthOfYearWithEvents,
    dateRange,
  };
};

/**
 * Get the default date range based on the nextTwoMonthsOnly flag
 * @param firstMonthWithEvents
 * @param nextTwoMonthsOnly
 * @returns
 */
const getDefaultDateRange = (
  firstMonthWithEvents: string,
  nextNMonths: number,
) => {
  return nextNMonths > 0
    ? {
        from: format(new Date(), EVENT_DATE_FORMAT),
        to: format(addMonths(new Date(), nextNMonths), EVENT_DATE_FORMAT),
        isFullMonth: false,
      }
    : {
        from: firstMonthWithEvents,
        to: format(
          lastDayOfMonth(
            parse(firstMonthWithEvents, EVENT_DATE_FORMAT, new Date()),
          ),
          EVENT_DATE_FORMAT,
        ),
        isFullMonth: true,
      };
};

/**
 * Helper function to filter events based on a certain date range
 * @param events
 * @param dateRange
 * @returns
 */
const filterEventsByDateRange = (
  events: EventManyQuery["eventMany"],
  dateRange: DateRange,
) => {
  return events
    .filter(event => {
      /**
       * Filter events based of it's start date & occurrences
       */
      let isWithinRange = false;

      event.occurrences?.some(o => {
        if (o) {
          const isOccurrenceWithinRange = isWithinInterval(new Date(o), {
            start: new Date(dateRange.from),
            end: new Date(dateRange.to),
          });

          if (isOccurrenceWithinRange) {
            isWithinRange = true;
            return true;
          }
        }

        return false;
      });

      return isWithinRange;
    })
    .map(event => {
      /**
       * Filter the occurrences of events that are within the date range to
       * only include occurrences within the date range
       */
      const newOccurrences = event.occurrences?.filter(o => {
        if (o) {
          return isWithinInterval(new Date(o), {
            start: new Date(dateRange.from),
            end: new Date(dateRange.to),
          });
        }
        return false;
      });
      return { ...event, occurrences: newOccurrences };
    });
};
